import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableDots,
  Pagination,
  Chip,
  Button,
  enqueueSnackbar,
  gateModal,
  gateDeleteModal,
  Checkbox,
  TableIconsCell,
  gateExtendDateModal,
  SimpleTable,
  gateLoadModal,
  TableCellComment,
  gateOutFilterCreateModal,
  gateOutFilterDeleteModal,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../context';
import {
  filterWithUserId,
  formatContainerType,
  getGateOutFilterOptions,
  getGateOutOptions,
  momentTextFormat,
  showToRoles,
} from '../../utils';
import {
  useStatesWithFilterCounter,
  useSort,
  usePaginationState,
  useGateOutsQuery,
  useCompleteGateOutMutation,
  useLoadedGateOutMutation,
} from '../../hooks';
import { GateOutGetAllQuery } from '../../types';
import {
  TABLE,
  MESSAGES,
  GateStatusToChipLabels,
  GateStatusToChipColors,
  ContainerConditionToChipLabels,
  ContainerConditionToChipColors,
} from '../../const';
import { i18n } from '../../i18n';
import { handleExportExcelClick } from './handle-export-excel-click';

interface GateOutsPageProps {
  innerScroll?: boolean;
  gridTemplateRows?: string;
  withBottomGap?: boolean;
}

export const GateOutsPage = styled.div<GateOutsPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}

  ${(props) =>
    props.gridTemplateRows &&
    css`
      grid-template-rows: ${props.gridTemplateRows};
    `}

  ${(props) =>
    props.withBottomGap &&
    css`
      &::after {
        content: '';
        display: block;
        height: 0.1rem;
      }
    `}
`;

export const GateOutsHeader = ({ totalCount, handleExportExcelClick }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.gateOuts.title')}: {totalCount}
      </Typography>
      <Button size={'medium'} onClick={handleExportExcelClick} disabled={!totalCount}>
        {i18n.t('button.exportExcel')}
      </Button>
      {!showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} to={'/dashboard/gateouts/add'}>
          {i18n.t('button.createRequest')}
        </Button>
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  data: GateOutGetAllQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const filteredExpiredData = data
    .filter((_: any, i: number) => checkIndexs.includes(i))
    .filter((item) => item.gateOut.status === 'EXPIRED');
  const filteredLoadingAllowedData = data
    .filter((_: any, i: number) => checkIndexs.includes(i))
    .filter((item) => item.gateOut.status === 'ACTIVE');
  const filteredDeleteData = data
    .filter((_: any, i: number) => checkIndexs.includes(i))
    .filter((item) =>
      ['ACTIVE', 'LOADING_ALLOWED', 'LOADED', 'EXPIRED'].includes(item.gateOut.status),
    );

  const handleClickAction = (val: any) => {
    if (val === 'extend-date') {
      gateExtendDateModal.ids = filteredExpiredData.map((item) => item.id);
      gateExtendDateModal.numbers = filteredExpiredData.map((item) => item.number);

      gateExtendDateModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.EXTENDED, 'success');
        refetch();
      };
      gateExtendDateModal.open();
      return;
    }
    if (val === 'loading-allowed') {
      gateLoadModal.data = filteredLoadingAllowedData.map((item) => ({
        id: item.id,
        number: item.number,
      }));

      gateLoadModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.LOADING_ALLOWED, 'success');
        refetch();
      };
      gateLoadModal.open();
      return;
    }
    if (val === 'delete') {
      if (!filteredDeleteData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      gateDeleteModal.data = filteredDeleteData.map((item) => ({
        id: item.id,
        number: item.number,
      }));
      gateDeleteModal.isGateIn = false;

      gateDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.DELETED, 'success');
        refetch();
      };
      gateDeleteModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      {!!filteredExpiredData.length && (
        <Button
          size={'medium'}
          data-color={'white'}
          onClick={() => handleClickAction('extend-date')}
        >
          {i18n.t('dropdown.extendDate')}
        </Button>
      )}
      {!!filteredLoadingAllowedData.length && (
        <Button
          size={'medium'}
          data-color={'white'}
          onClick={() => handleClickAction('loading-allowed')}
        >
          {i18n.t('dropdown.loadingAllowed')}
        </Button>
      )}
      {!!filteredDeleteData.length && (
        <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('delete')}>
          {i18n.t('button.delete')}
        </Button>
      )}
    </>
  );
};

export const GateOutsBody = ({
  setData,
  setTotalCount,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  setExportHandler,
  onPrintReceipt,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();

  const { page, take, skip, handlePageChange } = usePaginationState();
  const {
    page: secondPage,
    take: secondTake,
    skip: secondSkip,
    handlePageChange: secondHandlePageChange,
  } = usePaginationState('second');

  const completeMutation = useCompleteGateOutMutation();
  const loadedMutation = useLoadedGateOutMutation();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      containerNumber: '',
      containerType: 'All',
      yardCompanyId: '',
      country: '',
      city: '',
      yardId: '',
      clientId: '',
      createdAt: null,
      startDate: null,
      endDate: null,
      completedAt: null,
      driverName: '',
      licensePlateNumber: '',
      status: 'All',
    },
    { status: 'ACTIVE' },
  );
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: filteredStates.status === 'COMPLETED' ? 'completedAt' : 'createdAt',
    sortDirection: 'desc',
  });

  const {
    data = { data: [], totalCount: 0, secondData: [], secondTotalCount: 0 },
    isFetching,
    refetch,
  } = useGateOutsQuery({
    ...filterWithUserId(filteredStates, user),
    take,
    skip,
    secondTake,
    secondSkip,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    if (!setExportHandler) return;
    setExportHandler(
      () => () =>
        handleExportExcelClick({
          states,
          user,
          filteredStates,
          totalCount: data.totalCount,
          sortField,
          sortDirection,
        }),
    );
    return () => setExportHandler(null);
  }, [setExportHandler, states, user, filteredStates, data.totalCount, sortField, sortDirection]);

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
    setData(data.data);
  }, [isFetching]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (id: any, val: any, i: any) => {
    Object.keys(gateModal).forEach((key) => {
      if (key !== 'open') delete gateModal[key];
    });

    gateModal.val = val;
    gateModal.containerNumber = data.data[i].number;

    if (val === 'loading-allowed') {
      gateLoadModal.data = [
        {
          id,
          number: data.data[i].number,
        },
      ];
      gateLoadModal.isGateOutFilter = false;

      gateLoadModal.submitClick = () => {
        setCheckIndexs([]);
        refetch();
      };
      gateLoadModal.open();
      return;
    }
    if (val === 'extend-date') {
      gateExtendDateModal.ids = [id];
      gateExtendDateModal.numbers = [data.data[i].number];

      gateExtendDateModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.EXTENDED, 'success');
        refetch();
      };
      gateExtendDateModal.open();
      return;
    }
    if (val === 'complete' || val === 'loaded') {
      gateModal.type = 'GATE_OUT';
      gateModal.containerType = data.data[i].type;
      gateModal.licensePlateNumber =
        data.data[i].gateOut.driver?.licensePlateNumber ||
        states.licensePlateNumber ||
        data.data[i].gateOut.authorizedLicensePlateNumbers[0];
      gateModal.authorizedLicensePlateNumbers = data.data[
        i
      ].gateOut.authorizedLicensePlateNumbers.map((val: any) => ({ label: val, value: val }));
      gateModal.driverFirstName = data.data[i].gateOut.driver?.firstName;
      gateModal.driverLastName = data.data[i].gateOut.driver?.lastName;
      gateModal.comment = data.data[i].gateOut.comment;

      gateModal.fc = (printAfterComplete = false) => {
        if (val === 'loaded') {
          loadedMutation.mutate(id, {
            onSuccess: () => {
              setCheckIndexs([]);
              enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.LOADED, 'success');
              refetch();
            },
          });
          return;
        }

        completeMutation.mutate(id, {
          onSuccess: () => {
            setCheckIndexs([]);
            enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.COMPLETED, 'success');
            refetch();
            if (printAfterComplete) onPrintReceipt(id);
          },
          onError: () => enqueueSnackbar(MESSAGES.GATE_OUT.ERROR.NOT_COMPLETED, 'error'),
        });
      };

      gateModal.open();
      return;
    }
    if (val === 'delete') {
      gateDeleteModal.data = [
        {
          id,
          number: data.data[i].number,
        },
      ];
      gateDeleteModal.isGateIn = false;

      gateDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.DELETED, 'success');
        refetch();
      };
      gateDeleteModal.open();
      return;
    }
  };

  const handleSecondDotsOptionSelect = (val: any, i: any) => {
    if (val === 'loading-allowed') {
      gateLoadModal.data = [
        {
          id: data.secondData[i].id,
          number: '',
        },
      ];
      gateLoadModal.isGateOutFilter = true;

      gateLoadModal.submitClick = () => {
        setCheckIndexs([]);
        refetch();
      };
      gateLoadModal.open();
      return;
    }
    if (val === 'create') {
      gateOutFilterCreateModal.id = data.secondData[i].id;
      gateOutFilterCreateModal.clientId = data.secondData[i].clientYard?.clientId;
      gateOutFilterCreateModal.yardId = data.secondData[i].clientYard?.yardId;
      gateOutFilterCreateModal.clientCode = data.secondData[i].clientYard?.client.code;
      gateOutFilterCreateModal.containerIds = data.secondData[i].containerIds;
      gateOutFilterCreateModal.condition = data.secondData[i].condition;
      gateOutFilterCreateModal.requestedCount = data.secondData[i].requestedCount;
      gateOutFilterCreateModal.type = data.secondData[i].type;
      gateOutFilterCreateModal.submitClick = () => {
        setCheckIndexs([]);
        refetch();
      };
      gateOutFilterCreateModal.open();
    }
    if (val === 'delete') {
      gateOutFilterDeleteModal.clientCode = data.secondData[i].clientYard?.client.code;
      gateOutFilterDeleteModal.id = data.secondData[i].id;
      gateOutFilterDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        refetch();
      };
      gateOutFilterDeleteModal.open();
    }
  };

  const handleRowClick = (to: string) => {
    if (openDotsMenuIndex !== null) return;
    if (checkIndexs.length) return;
    navigate(to);
  };

  return (
    <>
      <SimpleTable
        data={data}
        filters={() => (
          <Filters handleSort={handleSort} setStates={handleFilterChange} {...states} />
        )}
        checkboxActions={(data) => (
          <CheckboxActions
            checkIndexs={checkIndexs.filter(
              (i: number) => data[i]?.gateOut?.status !== 'COMPLETED',
            )}
            data={data}
            refetch={refetch}
            setCheckIndexs={setCheckIndexs}
          />
        )}
        tableRowProps={(container, i) => ({
          key: container.number + i,
          onClick: () => handleRowClick(`/dashboard/gateouts/${container.id}`),
        })}
        columns={[
          {
            column: 1,
            head: () => (
              <TableHeadCell align={'left'} withGap>
                {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user) && (
                  <Checkbox
                    indeterminate={!!checkIndexs.length && checkIndexs.length !== data.data.length}
                    checked={checkIndexs.length === data.data.length}
                    onChange={() => handleCheckboxChange()}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {i18n.t('label.actions')}
              </TableHeadCell>
            ),
            body: (container, i) => (
              <TableIconsCell component={'th'} align={'left'}>
                {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user) && (
                  <Checkbox
                    checked={checkIndexs.includes(i)}
                    onChange={() => handleCheckboxChange(i)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {!!getGateOutOptions(container, user).length && (
                  <TableDots
                    open={openDotsMenuIndex === i}
                    onToggle={() => handleDotsToggle(i)}
                    options={getGateOutOptions(container, user)}
                    onSelect={(val) => {
                      handleDotsOptionSelect(container.id, val, i);
                    }}
                  />
                )}
              </TableIconsCell>
            ),
          },
          {
            headText: () => i18n.t('common.client'),
            bodyText: (container) => container.clientYard?.client.code,
            isHidden: () => !showToRoles('adminRead', user),
          },
          {
            headText: () => i18n.t('label.containerNumber'),
            bodyText: (container) => container.number,
          },
          {
            headText: () => i18n.t('label.type'),
            bodyText: (container) => formatContainerType(container.type),
          },
          {
            headText: () => i18n.t('label.status'),
            body: (container) => (
              <TableCell align={'left'}>
                <Chip
                  size={'small'}
                  label={GateStatusToChipLabels[container.gateOut.status]}
                  color={GateStatusToChipColors[container.gateOut.status]}
                />
              </TableCell>
            ),
          },
          {
            head: () => (
              <TableHeadCell
                align={'left'}
                columnIdx={'createdAt'}
                sortField={sortField}
                sortDirection={sortDirection}
                handleSort={handleSort}
              >
                {i18n.t('label.createdAt')}
              </TableHeadCell>
            ),
            bodyText: (container) =>
              container.gateOut.createdAt
                ? momentTextFormat(container.gateOut.createdAt, user)
                : '',
          },
          {
            isHidden: () => !(states.status === 'All' || states.status === 'COMPLETED'),
            head: () => (
              <TableHeadCell
                align={'left'}
                columnIdx={'completedAt'}
                sortField={sortField}
                sortDirection={sortDirection}
                handleSort={handleSort}
              >
                {i18n.t('label.completedAt')}
              </TableHeadCell>
            ),
            bodyText: (container) =>
              container.gateOut.completedAt
                ? momentTextFormat(container.gateOut.completedAt, user)
                : '',
          },
          {
            headText: () => i18n.t('label.gateOutDateFrom'),
            bodyText: (container) =>
              container.gateOut.validFrom
                ? momentTextFormat(container.gateOut.validFrom, user)
                : (states.status === 'All' || states.status === 'COMPLETED') &&
                  container.gateOut.createdAt
                ? momentTextFormat(container.gateOut.createdAt, user)
                : '',
          },
          {
            headText: () => i18n.t('label.gateOutDateTo'),
            bodyText: (container) =>
              container.gateOut.validTo
                ? momentTextFormat(container.gateOut.validTo, user)
                : (states.status === 'All' || states.status === 'COMPLETED') &&
                  container.gateOut.completedAt
                ? momentTextFormat(container.gateOut.completedAt, user)
                : '',
          },
          {
            isHidden: () => states.status !== 'COMPLETED',
            headText: () => i18n.t('label.licensePlateNumber'),
            bodyText: (container) => container.gateOut.driver?.licensePlateNumber,
          },
        ]}
        footer={() => (
          <TableWrapperFooter>
            <Pagination
              count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
              page={page}
              onChange={handleOnPageChange}
            />
          </TableWrapperFooter>
        )}
      />
      <SimpleTable
        id={'table-with-requests'}
        data={{ data: data.secondData, totalCount: data.secondTotalCount }}
        tableRowProps={(item) => ({
          key: item.id,
          onClick: () => {},
        })}
        columns={[
          {
            column: 1,
            head: () => <TableHeadCell align={'left'}>{i18n.t('label.actions')}</TableHeadCell>,
            body: (container, i) => (
              <TableIconsCell component={'th'} align={'left'}>
                <TableDots
                  open={openDotsMenuIndex === data.totalCount + i}
                  onToggle={() => handleDotsToggle(data.totalCount + i)}
                  options={getGateOutFilterOptions(container, user)}
                  onSelect={(val) => handleSecondDotsOptionSelect(val, i)}
                />
              </TableIconsCell>
            ),
          },
          {
            headText: () => i18n.t('common.client'),
            bodyText: (container) => container.clientYard?.client.code,
            isHidden: () => !showToRoles('adminRead', user),
          },
          {
            column: 15,
            headText: () => i18n.t('label.containerNumbers'),
            body: (container) => (
              <TableCellComment comment={container.containerNumbers.join(', ')} />
            ),
            isHidden: (container) => !container.containerNumbers.length,
          },
          {
            column: 15,
            headText: () => i18n.t('label.licensePlateNumber'),
            body: (container) => (
              <TableCellComment comment={container.authorizedLicensePlateNumbers.join(', ')} />
            ),
          },
          {
            headText: () => i18n.t('label.condition'),
            body: (container) => (
              <TableCell align={'left'}>
                {container.condition && (
                  <Chip
                    size={'small'}
                    label={ContainerConditionToChipLabels[container.condition]}
                    color={ContainerConditionToChipColors[container.condition]}
                  />
                )}
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.requestedCount'),
            bodyText: (container) => container.requestedCount?.toString() || i18n.t('label.all'),
          },
          {
            headText: () => i18n.t('label.type'),
            bodyText: (container) => formatContainerType(container.type),
          },
          {
            headText: () => i18n.t('label.status'),
            body: (container) => (
              <TableCell align={'left'}>
                <Chip
                  size={'small'}
                  label={GateStatusToChipLabels[container.status]}
                  color={GateStatusToChipColors[container.status]}
                />
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.gateOutDateFrom'),
            bodyText: (container) => momentTextFormat(container.validFrom, user),
          },
          {
            headText: () => i18n.t('label.gateOutDateTo'),
            bodyText: (container) => momentTextFormat(container.validTo, user),
          },
        ]}
        footer={() => (
          <TableWrapperFooter>
            <Pagination
              count={Math.ceil(data.secondTotalCount / TABLE.PAGE_MAX_SIZE)}
              page={secondPage}
              onChange={secondHandlePageChange}
            />
          </TableWrapperFooter>
        )}
      />
    </>
  );
};
